import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, DatePicker, Statistic, Row, Col } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePW9321Query } from './hooks/usePW9321Query';
import type { LeaveRow } from '../../services/pw9321Api';
import './PW9321Form.css';

const { RangePicker } = DatePicker;

const PW9321Form: React.FC = () => {
  const { loading, data, totalDay, queried, noData, runQuery, reset } = usePW9321Query();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');
  const [leaveType, setLeaveType] = useState('');

  const handleQuery = () => {
    runQuery({
      dateFrom: dateRange?.[0]?.format('YYYY-MM-DD') || undefined,
      dateTo: dateRange?.[1]?.format('YYYY-MM-DD') || undefined,
      deptFrom: deptFrom.trim() || undefined,
      deptTo: deptTo.trim() || undefined,
      leaveType: leaveType.trim() || undefined,
    });
  };
  const handleReset = () => { setDateRange(null); setDeptFrom(''); setDeptTo(''); setLeaveType(''); reset(); };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>請假明細表</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 4px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>請假明細表</h2><p style="text-align:center">總請假天數: ${totalDay.toFixed(2)}</p>
<table><thead><tr><th>單號</th><th>員工</th><th>姓名</th><th>部門</th><th>假別</th><th>請假日</th><th>起</th><th>迄日</th><th>迄</th><th>數量</th><th>單位</th></tr></thead><tbody>
${data.map(r=>`<tr><td>${r.doc_no}</td><td>${r.emp_no}</td><td>${r.emp_name}</td><td>${r.dept_name}</td><td>${r.leave_type}</td><td>${r.leave_date?.substring(0,10)||''}</td><td>${r.start_time}</td><td>${r.end_date?.substring(0,10)||''}</td><td>${r.end_time}</td><td class="right">${r.duration}</td><td>${r.unit}</td></tr>`).join('')}
</tbody></table></body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  const columns: ColumnsType<LeaveRow> = [
    { title: '單號', dataIndex: 'doc_no', key: 'doc_no', width: 100 },
    { title: '員工代號', dataIndex: 'emp_no', key: 'emp_no', width: 90 },
    { title: '員工姓名', dataIndex: 'emp_name', key: 'emp_name', width: 100 },
    { title: '部門', dataIndex: 'dept_name', key: 'dept_name', width: 100 },
    { title: '假別', dataIndex: 'leave_type', key: 'leave_type', width: 60 },
    { title: '請假日', dataIndex: 'leave_date', key: 'leave_date', width: 100, render: (v: string) => v?.substring(0,10)||'' },
    { title: '起時', dataIndex: 'start_time', key: 'start_time', width: 60 },
    { title: '迄日', dataIndex: 'end_date', key: 'end_date', width: 100, render: (v: string) => v?.substring(0,10)||'' },
    { title: '迄時', dataIndex: 'end_time', key: 'end_time', width: 60 },
    { title: '數量', dataIndex: 'duration', key: 'duration', width: 70, align: 'right' as const },
    { title: '單位', dataIndex: 'unit', key: 'unit', width: 50 },
    { title: '事由', dataIndex: 'reason', key: 'reason', ellipsis: true },
  ];

  return (
    <PermissionGuard module={Modules.PW9321} action="query">
      <div className="pw9321-container">
        <div className="pw9321-header">
          <h2>請假明細表</h2>
          {queried && !noData && <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>請假日期範圍</label>
              <RangePicker value={dateRange} onChange={val => setDateRange(val as [Dayjs|null,Dayjs|null]|null)} style={{ width: 260 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>部門起</label>
              <Input value={deptFrom} onChange={e => setDeptFrom(e.target.value)} placeholder="起始" style={{ width: 100 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>部門迄</label>
              <Input value={deptTo} onChange={e => setDeptTo(e.target.value)} placeholder="結束" style={{ width: 100 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>假別</label>
              <Input value={leaveType} onChange={e => setLeaveType(e.target.value)} placeholder="假別代碼" style={{ width: 80 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無符合條件的請假資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={24}>
                <Col span={6}><Statistic title="總筆數" value={data.length} /></Col>
                <Col span={6}><Statistic title="總請假天數" value={totalDay.toFixed(2)} /></Col>
              </Row>
            </Card>
            <Table<LeaveRow> columns={columns} dataSource={data} rowKey="doc_no" loading={loading} size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }} scroll={{ x: 1100 }} />
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default PW9321Form;
